// NPM
import { defineStore } from 'pinia';

// Services
import Api from '@/services/api';
import { persistJwtToken } from '@/services/auth-tokens';

// Stores
import { useUserStore } from '@/stores/user';

// Types
import type { Tracking } from '@/helpers/tracking';
import type { IPublicOrganizationData } from '@/types/IPublicOrganizationData';
import type {
  UserDashboardJwtTokenResponseType,
  ProspectDashboardGetJwtTokenResponseType,
  GenericLoginGetResponseType,
  SignUpProspectGetQualificationResponseType,
  SignUpProspectGetReferredResponseType,
  SharingGetSharingDataResponseType,
  SignUpProspectPostEmailResponseType,
  SignUpProspectUpdateProspectMainInformationResponseType,
  SignUpProspectUpdateOtherCriteriaResponseType,
  SignUpProspectUpdateProspectMainInformationRequestType,
  SignUpProspectUpdateExpectationRequestType,
  SignUpProspectPostReferredRequestType,
} from '@magma-app/magma-types/all';

export const useAuthStore = defineStore('auth', {
  state: () => {
    return {
      helpeePostCompleteSignUpData:
        null as SignUpProspectUpdateOtherCriteriaResponseType | null,
      helpeePostMatchData:
        null as SignUpProspectUpdateProspectMainInformationResponseType | null,
      helpeePostSignUpData: null as SignUpProspectPostEmailResponseType | null,
      helpeeSignUpData: null as IPublicOrganizationData | null,
      jwtToken: null as string | null,
      referredSharingRelationUrl: null as string | null,
      referredSharingSignUpData:
        null as SharingGetSharingDataResponseType | null,
      referredSignUpData: null as SignUpProspectGetReferredResponseType | null,
      signInData: null as IPublicOrganizationData | null,
      loginAs: null as GenericLoginGetResponseType | null,
      haveSendPhoneCode: false as boolean,
      codeValidated: null as {
        message: string;
        error?: boolean;
        errorType?: string;
      } | null,
      helpeeQualificationData:
        null as SignUpProspectGetQualificationResponseType | null,
      haveSendExpectation: false as boolean,
    };
  },

  actions: {
    async getJwtToken(token: string, isProspect: boolean) {
      if (token) {
        const userStore = useUserStore();
        const response = await Api.userDashboard().auth.getJwtToken(
          {
            token,
          },
          isProspect
        );

        this.jwtToken = response.data.jwtToken;
        if (isProspect) {
          userStore.$patch({
            user: (response.data as ProspectDashboardGetJwtTokenResponseType)
              .prospect,
          });
        } else {
          userStore.$patch({
            user: (response.data as UserDashboardJwtTokenResponseType).user,
          });
        }

        // Save JWT Token in local storage and cookies (also clears other persona tokens)
        persistJwtToken(this.jwtToken, isProspect ? 'prospect' : 'user', true);
      }
    },

    async getSignInData(
      organizationSubdomainName: string,
      campaignUuid: string
    ) {
      const response = await Api.userDashboard().auth.getSignInData(
        organizationSubdomainName,
        campaignUuid
      );
      this.signInData = response?.data;
    },

    async signIn(email: string, redirect?: string) {
      await Api.main().auth.signIn({ email, redirect });
    },

    async signInAs(
      email: string,
      loginAs?: 'ambassador' | 'admin' | 'prospect',
      organizationUuid?: string,
      redirect?: string
    ) {
      const response = await Api.main().auth.signInAs({
        email,
        loginAs,
        organizationUuid,
        redirect,
      });

      this.loginAs = response.data;
    },

    async getHelpeeSignUpData(payload: {
      campaignUuid?: string;
      organizationUuid?: string;
    }) {
      const response =
        await Api.userDashboard().auth.getHelpeeSignUpData(payload);

      this.helpeeSignUpData = response.data;
    },

    async getReferredSignUpData(referredUuid: string) {
      const response = await Api.main().referral.getReferredSignUpData({
        referredUuid,
      });

      this.referredSignUpData = response.data;
    },

    async getReferredSharingSignUpData(shareId: string) {
      const response = await Api.main().referral.getReferredSharingSignUpData({
        shareId,
      });

      this.referredSharingSignUpData = response.data;
    },

    async sendReferredSignUpData(
      payload: SignUpProspectPostReferredRequestType,
      turnstileToken?: string
    ) {
      await Api.main().referral.sendReferredSignUpData(payload, turnstileToken);
    },

    async sendReferredSharingSignUpData(payload: {
      campaignUuid: string;
      helperId: number;
      shareId: string;
      firstname: string;
      lastname: string;
      email: string;
      phoneNumber: string;
      strongProspectCriteria: {
        criteriaId: number;
        answerKey?: string;
        answerKeys?: string[];
      };
      prospectTouchPoints: {
        touchPointId: number;
        value: string;
      }[];
    }) {
      const response =
        await Api.main().referral.sendReferredSharingSignUpData(payload);

      this.referredSharingRelationUrl = response.data.url;
    },

    async signUpHelpee(payload: {
      campaignUuid: string;
      email: string;
      organizationId?: number;
      source: string;
      acquisitionSource: string;
      /** Free-form customer metadata from `window.magma_app[].tracking`. See `helpers/tracking`. */
      tracking?: Tracking;
    }, turnstileToken?: string) {
      const response = await Api.main().auth.signUpHelpee(payload, turnstileToken);

      this.helpeePostSignUpData = response.data;

      return response.data;
    },

    async completeSignUpHelpee(
      payload: SignUpProspectUpdateProspectMainInformationRequestType
    ) {
      const response = await Api.main().auth.completeSignUpHelpee(payload);

      this.helpeePostMatchData = response.data;

      return response.data;
    },

    async sendCodeRequest(payload: {
      phoneNumber: string;
      prospectUuid: string;
    }) {
      await Api.main().auth.sendCodeRequest(payload);

      this.haveSendPhoneCode = true;
    },

    async validateCode(payload: { prospectUuid: string; code: string }) {
      const response = await Api.main().auth.validateCode(payload);

      this.codeValidated = response.data;

      return response.data;
    },

    async addOptionalInfoSignUpHelpee(payload: {
      prospectUuid: string;
      expectation?: string;
      prospectCriteria: {
        criteriaId: number;
        answerKey?: string;
        answerKeys?: string[];
      }[];
    }) {
      const response =
        await Api.main().auth.addOptionalInfoSignUpHelpee(payload);

      this.helpeePostCompleteSignUpData = response.data;
    },

    async getCampaignHelpeeSignUpData(campaignHelpeeUuid: string) {
      const response = await Api.main().auth.getCampaignHelpeeSignUpData({
        prospectUuid: campaignHelpeeUuid,
      });

      this.helpeeQualificationData = response.data;
    },

    async sendExpectationSignUpHelpee(
      payload: SignUpProspectUpdateExpectationRequestType
    ) {
      await Api.main().auth.sendExpectationSignUpHelpee(payload);

      this.haveSendExpectation = true;
    },

    async sendTouchPointsSignUpHelpee(payload: {
      prospectUuid: string;
      touchPoints: { touchPointId: number; value: string }[];
      acceptEmailNotifications: boolean;
      acceptPhoneNotifications: boolean;
    }) {
      await Api.main().auth.sendTouchPointsSignUpHelpee(payload);
    },
  },
  persist: new URL(window.location.href).search.includes('?widget=true')
    ? false
    : true,
});
